
import { useState } from 'react';
import Sidebar from '../components/Sidebar';

interface Reservation {
  id: number;
  customerName: string;
  reservationType: string;
  reservationDate: string;
  reservationTime: string;
  location: string;
  participants: number;
  specialRequests: string;
  status: string;
  manager: string;
  createdDate: string;
}

export default function ReservationManagement() {
  const [reservations, setReservations] = useState<Reservation[]>([
    {
      id: 1,
      customerName: '이지은',
      reservationType: '웨딩홀 예약',
      reservationDate: '2024-03-15',
      reservationTime: '14:00',
      location: '강남웨딩홀 그랜드홀',
      participants: 150,
      specialRequests: '꽃장식 추가, 음향시설 점검',
      status: '확정',
      manager: '김플래너',
      createdDate: '2024-01-15'
    },
    {
      id: 2,
      customerName: '김민수',
      reservationType: '드레스 피팅',
      reservationDate: '2024-03-20',
      reservationTime: '10:00',
      location: '엘레강스 웨딩 강남점',
      participants: 2,
      specialRequests: '사이즈 조정 필요',
      status: '예정',
      manager: '박플래너',
      createdDate: '2024-02-01'
    },
    {
      id: 3,
      customerName: '박영희',
      reservationType: '메이크업 시연',
      reservationDate: '2024-03-25',
      reservationTime: '15:00',
      location: '뷰티살롱 청담점',
      participants: 1,
      specialRequests: '자연스러운 메이크업 선호',
      status: '예정',
      manager: '최플래너',
      createdDate: '2024-02-10'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedType, setSelectedType] = useState('전체');
  const [selectedStatus, setSelectedStatus] = useState('전체');

  const reservationTypes = ['전체', '웨딩홀 예약', '드레스 피팅', '메이크업 시연', '촬영 예약', '기타'];
  const statuses = ['전체', '확정', '예정', '완료', '취소'];

  const filteredReservations = reservations.filter(reservation => {
    const typeMatch = selectedType === '전체' || reservation.reservationType === selectedType;
    const statusMatch = selectedStatus === '전체' || reservation.status === selectedStatus;
    return typeMatch && statusMatch;
  });

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">예약관리</h1>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              예약 등록
            </button>
          </div>

          {/* 필터 */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <div className="flex gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">예약유형</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 pr-8"
                >
                  {reservationTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 pr-8"
                >
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 예약 목록 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">고객명</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">예약유형</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">예약일시</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">장소</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">참석인원</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">특별요청</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">상태</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">담당자</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredReservations.map((reservation) => (
                    <tr key={reservation.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{reservation.customerName}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{reservation.reservationType}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {reservation.reservationDate} {reservation.reservationTime}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{reservation.location}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{reservation.participants}명</td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{reservation.specialRequests}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          reservation.status === '확정' ? 'bg-green-100 text-green-800' :
                          reservation.status === '예정' ? 'bg-yellow-100 text-yellow-800' :
                          reservation.status === '완료' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {reservation.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{reservation.manager}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button className="text-blue-600 hover:text-blue-800 w-6 h-6 flex items-center justify-center">
                            <i className="ri-edit-line"></i>
                          </button>
                          <button className="text-green-600 hover:text-green-800 w-6 h-6 flex items-center justify-center">
                            <i className="ri-phone-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 예약 등록 모달 */}
          {showAddForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">예약 등록</h2>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
                
                <form className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">고객명</label>
                      <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예약유형</label>
                      <select className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8">
                        {reservationTypes.slice(1).map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예약일</label>
                      <input type="date" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예약시간</label>
                      <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">장소</label>
                    <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">참석인원</label>
                      <input type="number" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">담당자</label>
                      <select className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8">
                        <option>김플래너</option>
                        <option>박플래너</option>
                        <option>최플래너</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">특별요청사항</label>
                    <textarea 
                      className="w-full border border-gray-300 rounded-md px-3 py-2 h-24 resize-none"
                      placeholder="특별한 요청사항이 있으면 입력하세요"
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap"
                    >
                      등록
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
